import axios from 'axios';
import { useAuthStore } from '@/store/authStore';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3001',
  withCredentials: true,
});

/**
 * Endpoints that establish *who you are*, rather than acting as someone already
 * known.
 *
 * The API resolves a login within the school named by `X-School-ID` (AR-21).
 * Attaching this header from the persisted session would scope a sign-in to
 * whoever was signed in last: `/auth/login` has no redirect-if-authenticated
 * guard, so someone still holding a school-A session who signs in with valid
 * school-B credentials would be looked up in school A, match nothing, and be
 * told their credentials are invalid - and each retry would burn throttle budget
 * until they were locked out.
 *
 * A stale session must not narrow a fresh sign-in, so these paths never carry it.
 */
const IDENTITY_PATHS = ['/auth/login', '/auth/register', '/signup', '/sso/'];

function establishesIdentity(url: string | undefined): boolean {
  return url !== undefined && IDENTITY_PATHS.some((path) => url.includes(path));
}

// Request interceptor to add auth token and school ID
api.interceptors.request.use((config) => {
  const { token, user } = useAuthStore.getState();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (user?.schoolId && !establishesIdentity(config.url)) {
    config.headers['X-School-ID'] = user.schoolId;
  }
  return config;
});

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Don't redirect on 401 for login endpoint - let the login page handle it
    const isLoginRequest = error.config?.url?.includes('/auth/login');
    if (error.response?.status === 401 && !isLoginRequest) {
      useAuthStore.getState().logout();
      window.location.href = '/auth/login';
    }
    return Promise.reject(error);
  }
);

export default api;
